/**
 * 일정·CRM·ERP·인사·관리자 화면의 SPA 내비게이션: 같은 페이지 안의 이동(월 이동·필터·검색·월↔목록·
 * 허브 정렬/필터/탭)은 새로고침 없이 pushState + 부분 교체로 처리한다.
 *  - 루트(#calGrid/#calList/#projHub/#recList…)는 calendar-history.refreshGrid 가 제자리에서
 *    갈아끼운다 (리스너 유지, 뷰 전환 시엔 풀에 숨겨 두고 재사용).
 *  - 툴바·필터·칩 같은 부속 영역은 [data-spa-region] 으로 표시해 innerHTML 만 바꾼다.
 *  - 같은 섹션(/crm/*, /erp/*, /hr/*, /admin/*) 안의 다른 경로(목록 ↔ 레코드 페이지)는 페이지 컨테이너
 *    (main.admin-main > .container)를 통째로 교체한다; 사이드바·패널은 남는다.
 *    이때 `calendar:navigated` 의 detail.crossPath 가 true 라 모듈이 새 루트에 다시 붙는다.
 *    떠나기 전에 취소 가능한 `calendar:before-navigate` 를 보내므로 dirty 편집기가 막을 수 있다
 *    (브라우저의 뒤로·앞으로로 다른 경로에 갈 때도 — detail.popstate).
 *  - 이동 중엔 상단 진행 막대 + 루트 흐림(lazy load 느낌), 링크 hover 시 미리 받아 둔다.
 *  - 다른 페이지(허브↔달력)·다른 섹션(/crm ↔ /erp ↔ /calendar)으로의 이동은 일반 내비게이션.
 */
import { refreshGrid, refreshOrReload, pageSwapPending, takeDeferredRefresh, hasRefreshRoot, takeSwapScroll } from './calendar-history.js';

const cache = new Map(); // href → { html, at }
const PREFETCH_TTL = 20000;
// 경로가 달라도 SPA 로 오가는 섹션 (첫 경로 조각)
const CROSS_PATH_SECTIONS = ['crm', 'erp', 'hr', 'admin', 'evaluation'];
// 같은 섹션 조각을 쓰지만 제자리 교체로 오갈 수 없는 경로: 통째 새로고침으로 둔다.
// 출발지든 목적지든 하나라도 여기 걸리면 일반 내비게이션이다.
//  - /admin/erp: 전역 사이드바(spec 2026-09-13 §3) 이후로는 모든 화면이 같은
//    사이드바를 쓰므로 사이드바 어긋남 문제는 없다 — 이 화면은 관리자 섹션
//    경로이면서 렌더링은 erpRender 를 거쳐 ERP 모듈 스크립트·스타일을 실어
//    오므로, /admin/* 의 다른 설정 화면과 제자리 교체하면 그 잔재가 남는다.
//  - /admin/vault/totp: 공유 모듈 totp-vault.js 가 재진입을 지원하지 않는다. 도착 쪽만 막으면
//    떠날 때 그 모듈의 1초 타이머 두 개와 문서 리스너가 남아 detached DOM 을 계속 두드린다.
const SECTION_EXCLUDE_PREFIXES = ['/admin/erp', '/admin/vault/totp'];
function excludedPath(pathname) {
    return SECTION_EXCLUDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}
// 첫 조각은 다르지만 같은 사이드바(=같은 섹션)를 쓰는 경로: /directory 는 hr-sidebar 를
// 그대로 쓰므로 인사 화면들과 한 섹션으로 묶어 제자리 교체 대상에 넣는다.
const SECTION_ALIASES = { directory: 'hr' };
// 페이지가 아니라 파일을 돌려주는 경로 (CSV·ICS·증빙·첨부·인쇄). SPA 가 가로채면
// 파일을 한 번 헛되이 받고 swapPage 가 실패해, 주소창만 내려받기 주소로 남는다
// (되돌릴 pushState 는 이미 끝난 뒤다). 링크 쪽 data-no-spa 와 별개로 경로 모양
// 만으로도 걸러, 새 다운로드 링크가 같은 함정을 다시 만들 수 없게 한다.
const DOWNLOAD_SEGMENTS = ['/export', '/files', '/download', '/csv', '/print', '/backup'];
const DOWNLOAD_EXTENSIONS = /\.(csv|ics|pdf|xlsx|zip)$/i;
function downloadPath(pathname) {
    if (DOWNLOAD_EXTENSIONS.test(pathname)) return true;
    return DOWNLOAD_SEGMENTS.some((seg) => pathname.endsWith(seg) || pathname.includes(seg + '/'));
}
let navSeq = 0;
// 경로가 바뀌는 이동(페이지 통째 교체)이 시작됐지만 화면이 아직 그 페이지로 바뀌지 않았으면 그 navSeq —
// fetch 중이든 refreshGrid 가 새 화면의 스타일을 기다리든. 그동안 들어온 이동은 경로가 같아도(같은 링크를
// 두 번 누르기) 페이지 통째 교체여야 한다: 루트 교체는 옛 껍데기에 새 루트를 심어 두 화면을 섞었다 (R31).
let swapInFlight = 0;
let currentPath = window.location.pathname;
// 지금 그려져 있는 화면의 주소와 그 기록 칸의 자리 — 조각 이동 판정과, 뒤로·앞으로 가기를
// 떠나기 확인이 막았을 때 되돌아갈 곳이다(백로그 BG8). 모듈이 스스로 pushState·replaceState 로
// 고친 조각·쿼리(#c-…, ?step=, ?deal=)도 여기에 든다 — trackHistory 가 두 호출을 감싼다(BH1·BH2).
// 기록 상태의 순번 열쇠와 그 모드의 두 값 — 아래 entryId() 가 모듈을 읽는 순간 쓰므로 먼저 둔다.
const SPA_IDX = '__spaIdx';
let lastIdx = null; // 순번 모드에서 마지막으로 본 칸의 순번 — 순번 없는 칸에 이어 줄 번호
let rawReplace = null; // 감싸기 전의 history.replaceState
let shownHref = window.location.href;
let shownId = entryId();
// 막힌 뒤로·앞으로 가기를 되감는 중이면 돌아갈 칸의 신원 — 그 popstate 는 화면을 건드리지 않는다(BH7).
let revertTo = null;

// 기록 칸의 신원. Navigation API 가 있으면 그 칸의 key 이고 되감기는 navigation.traverseTo(key) 다 —
// history.go 의 걸음 수는 iframe 의 칸까지 센(joint session history) 것이라 Navigation API 의 자리
// 차와 다를 수 있다(BI1). 없는 브라우저에서는 라우터가 기록 상태에 넣는 순번(SPA_IDX)이고 되감기는
// 그 차만큼의 history.go 다: trackHistory 가 pushState 마다 순번을 하나 올리고 replaceState 는 순번을
// 잇는다(모듈이 만든 칸도 같은 감싸개를 지난다). 순번이 없는 칸(관찰하지 못한 조각 이동)에서만
// 예전처럼 지금 주소를 새 칸으로 밀어 넣는다(앞 칸이 잘린다).
function navEntry() {
    try {
        const entry = window.navigation && window.navigation.currentEntry;
        return entry && entry.index >= 0 && entry.key ? entry : null;
    } catch (_) { return null; }
}
function stateIndex(state) {
    return state && typeof state === 'object' && typeof state[SPA_IDX] === 'number' ? state[SPA_IDX] : null;
}
function withIndex(state, idx) {
    if (idx == null) return state;
    if (state == null) return { [SPA_IDX]: idx };
    if (typeof state !== 'object' || Array.isArray(state)) return state;
    return Object.assign({}, state, { [SPA_IDX]: idx });
}
function entryId() {
    const entry = navEntry();
    if (entry) return entry.key;
    if (window.navigation && window.navigation.currentEntry) return null; // API 는 있는데 자리를 모른다
    return stateIndex(window.history.state);
}
function noteShown() {
    shownHref = window.location.href;
    shownId = entryId();
    if (typeof shownId === 'number') lastIdx = shownId;
}
function trackHistory() {
    const h = window.history;
    if (h.__spaTracked) return;
    h.__spaTracked = true;
    rawReplace = h.replaceState;
    ['pushState', 'replaceState'].forEach((name) => {
        const orig = h[name];
        h[name] = function (...args) {
            if (!window.navigation) {
                const cur = stateIndex(h.state);
                if (name === 'pushState') {
                    // 떠나는 칸에 순번이 없으면(첫 칸 · 관찰하지 못한 조각 칸) 먼저 이어 준다.
                    const from = cur != null ? cur : (lastIdx == null ? 0 : lastIdx + 1);
                    if (cur == null) rawReplace.call(h, withIndex(h.state, from), '', window.location.href);
                    args[0] = withIndex(args[0], from + 1);
                } else if (cur != null) {
                    args[0] = withIndex(args[0], cur);
                }
            }
            const out = orig.apply(this, args);
            // 되감는 동안의 replaceState 는 막힌(떠나려던) 칸을 고친다 — 보이는 화면은 그대로다.
            if (name === 'pushState' || revertTo == null) noteShown();
            // 새 칸이 생겼으면 기다리던 되감기의 목적지는 사라졌다(앞 칸이 잘렸다) — 그 되감기는 아무
            // popstate 도 내지 않으므로 여기서 놓는다. 쥐고 있으면 나중의 진짜 뒤로 가기를 삼킨다(최종 리뷰 2).
            if (name === 'pushState') revertTo = null;
            return out;
        };
    });
}
// 되감기가 도는 동안(그 칸의 popstate · 실패 · 안전 한도까지)은 이동을 미룬다 — 되감기 중에 새 칸을
// 밀면, 절대 목적지인 key 되감기(traverseTo)가 뒤늦게 커밋되어 보이던 화면으로 끌고 갔다(막힌 앞으로
// 가기 뒤 그 사이 끝난 저장의 이동, BI 최종 리뷰 1). 되감은 뒤의 이동은 보이는 화면의 칸에서 민다.
const REVERT_WAIT_MS = 1500;
let revertWait = null;
function beginRevert(target) {
    endRevert();
    revertTo = target;
    let settle = null;
    const promise = new Promise((resolve) => { settle = resolve; });
    const timer = setTimeout(endRevert, REVERT_WAIT_MS);
    revertWait = { promise, settle: () => { clearTimeout(timer); settle(); } };
}
function endRevert() {
    const w = revertWait;
    revertWait = null;
    if (w) w.settle();
}
// 막힌 뒤로·앞으로 가기를 보이는 화면의 칸으로 되감는다. 되감지 못하면(칸을 모름 · 그 칸이 사라짐)
// 예전처럼 지금 주소를 새 칸으로 밀어 넣는다.
function revertToShown() {
    const at = entryId();
    const target = shownId;
    const pushBack = () => window.history.pushState({ spa: true }, '', shownHref);
    if (at == null || target == null || at === target || typeof at !== typeof target) { pushBack(); return; }
    beginRevert(target);
    if (typeof target === 'number') { window.history.go(target - at); return; }
    let res = null;
    try { res = window.navigation.traverseTo(target); } catch (_) { res = null; }
    const failed = (err) => {
        if (err && err.name === 'AbortError') return; // 다른 이동이 끼어들었다 — 그 이동이 정한다
        if (revertTo !== target) return;
        revertTo = null;
        endRevert();
        pushBack();
    };
    if (!res) { failed(null); return; }
    res.committed.catch(failed);
    res.finished.catch(() => {});
}

// 지금 **그려져 있는** 화면의 경로 — 주소가 아니다. 막힌 뒤로 가기를 되감는 동안(history.go 는
// 비동기다) 주소는 뒤로 간 쪽이지만 화면은 그대로이므로, "사용자가 이 화면을 떠났나" 는 이것으로
// 판단한다(업무 평가의 pageActions, 최종 리뷰 3).
export function shownPath() { return currentPath; }

function rawSection(pathname) {
    const m = /^\/([^/]+)/.exec(pathname || '');
    return m ? m[1] : '';
}
function sectionOf(pathname) {
    const s = rawSection(pathname);
    return SECTION_ALIASES[s] || s;
}
// 예전에는 별칭으로 한 섹션이 된 경로(/hr ↔ /directory)에 여기서 직접 ?_from= 을
// 붙였다. 인사 화면 중 back-link 파셜을 그리는 것은 하나도 없어서 그 파라미터는
// 아무것도 만들지 못한 채 주소창에만 남았고, back-link.js 가 지키는 data-no-back 도
// 무시했다. 그래서 SPA 는 링크 주소를 손대지 않는다 — ?_from= 을 붙일지는 오직
// back-link.js 의 규칙(그리고 그 규칙이 존중하는 data-no-back)만 결정한다.
function samePage(href) {
    let u;
    try { u = new URL(href, window.location.href); } catch (_) { return false; }
    return u.origin === window.location.origin && u.pathname === window.location.pathname && !u.hash;
}
// 같은 페이지이거나, 같은 CRM/ERP 섹션 안의 다른 경로 (레코드 페이지·편집기)
export function spaTarget(href) {
    let u;
    try { u = new URL(href, window.location.href); } catch (_) { return false; }
    if (u.origin !== window.location.origin) return false;
    if (downloadPath(u.pathname)) return false;
    if (excludedPath(u.pathname) || excludedPath(window.location.pathname)) return false;
    if (u.pathname === window.location.pathname) return !u.hash;
    if (u.hash) return false;
    const sec = sectionOf(u.pathname);
    return CROSS_PATH_SECTIONS.includes(sec) && sec === sectionOf(window.location.pathname);
}
function fetchPage(href) {
    return fetch(href, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } }).then((r) => {
        if (!r.ok) throw new Error('nav failed');
        return r.text().then((html) => ({ html, url: r.url }));
    });
}
// 받아 둔 화면을 전부 버린다 — 저장으로 서버 상태가 바뀐 뒤, 그 전에 받아 둔 문서가
// 다음 이동에 쓰이지 않게 (인력정보 편집기가 저장 성공마다 부른다).
export function clearPrefetched() { cache.clear(); }
export function prefetch(href) {
    const hit = cache.get(href);
    if (hit && Date.now() - hit.at < PREFETCH_TTL) return hit.promise;
    const entry = { at: Date.now(), promise: fetchPage(href).catch(() => { cache.delete(href); return null; }) };
    cache.set(href, entry);
    return entry.promise;
}

// 지금 있는 항목을 "SPA 가 그린 화면" 으로 표시한다 — 뒤로 가기로 여기 돌아오면
// popstate 가 제자리 교체로 처리하도록. 예전에는 setupCalendarSpa() 에서 미리 했는데,
// 그러면 페이지가 로드될 때마다 same-document 내비게이션이 하나 생긴다: 클릭 뒤
// waitForNavigation 으로 "진짜 이동이었나" 를 가리는 코드·테스트가 그것을 이동으로
// 오해했다 (hr-navigation 의 모듈 스위치 검사). 떠나기 직전에만 표시한다.
function markCurrentEntry() {
    const st = window.history.state;
    if (!st || !st.spa) window.history.replaceState({ spa: true }, '', window.location.href);
}

// 제자리 교체가 끝난 뒤, 진행 표시(html.spa-loading)를 끄기 전에 기다릴 일을 한 군데서
// 받는다 — main.js 가 관리자 페이지 모듈(지연 로드)이 붙을 때까지 여기에 물린다.
let afterSwap = null;
export function setAfterSwapHook(fn) { afterSwap = fn; }

// swapSelectorOnly: opts.swap 이 문자열(선택자)이면 페이지 전체가 아니라 그 선택자에
// 맞는 요소들만 새 문서의 같은 id 요소로 바꾼다 (백로그 BE22, 2026-09-20). 저장 뒤
// "그 카드만 다시 받기" 가 HR·발령·ERP 원장에서 각자 구현되던 것을 한 길로 모은다.
// 규칙: id 가 있는 요소만 짝을 짓고, 새 문서에 짝이 없거나 하나도 못 찾으면 페이지
// 통째 교체(옛 swap:true)로 떨어진다; 스크롤·포커스는 건드리지 않고, 끝나면
// calendar:navigated 를 {swapped:[ids]} 와 함께 올린다. history 는 replace 만
// (같은 URL 을 다시 받는 용도라 push 할 이유가 없다).
function swapSelectorOnly(u, selector) {
    return fetchPage(u.href).then((p) => {
        const doc = new DOMParser().parseFromString(p.html, 'text/html');
        const targets = Array.from(document.querySelectorAll(selector)).filter((el) => el.id);
        const swapped = [];
        for (const el of targets) {
            const fresh = doc.getElementById(el.id);
            if (!fresh) continue;
            el.replaceWith(document.adoptNode(fresh));
            swapped.push(el.id);
        }
        return swapped;
    });
}

// 탭 틀이 탭을 닫기 전에 떠나기 가드를 거칠 때(workspace-frame.js canClose), 가드가 확인 뒤 부르는
// navigate(가짜 주소, {force}) 를 받아 "닫아도 된다" 로 바꾸는 자리. 그 밖에서는 비어 있다.
let navInterceptor = null;
export function setNavigateInterceptor(fn) { navInterceptor = typeof fn === 'function' ? fn : null; }

export function navigate(href, opts) {
    opts = opts || {};
    if (navInterceptor && navInterceptor(href, opts)) return Promise.resolve(false);
    if (revertWait) return revertWait.promise.then(() => navigate(href, opts));
    const u = new URL(href, window.location.href);
    if (typeof opts.swap === 'string' && opts.swap) {
        const root = document.documentElement;
        root.classList.add('spa-loading');
        return swapSelectorOnly(u, opts.swap)
            .then((swapped) => {
                if (!swapped.length) { root.classList.remove('spa-loading'); return navigate(href, Object.assign({}, opts, { swap: true, replace: true })); }
                if (u.href !== window.location.href) window.history.replaceState({ spa: true }, '', u.href);
                shownHref = window.location.href;
                document.dispatchEvent(new CustomEvent('calendar:navigated', { detail: { path: window.location.pathname, crossPath: false, swapped } }));
                return true;
            })
            .catch(() => { root.classList.remove('spa-loading'); return navigate(href, Object.assign({}, opts, { swap: true, replace: true })); })
            .finally(() => root.classList.remove('spa-loading'));
    }
    // E-18 §4.1: 경로가 같은 페이지 교체(저장 뒤 다시 그리기·같은 화면 GET 폼)만
    // main 의 스크롤을 잇는다. 경로가 바뀌는 이동(뒤로 가기 포함)은 맨 위에서 시작한다.
    // 앞 이동의 페이지 교체가 아직이면 화면은 currentPath 의 페이지가 아니다 — 같은 경로라도 통째 교체 (R31).
    const swapPending = swapInFlight !== 0 || pageSwapPending();
    const keepMainScroll = !!opts.keepScroll && u.pathname === currentPath && !swapPending;
    const crossPath = u.pathname !== currentPath || !!opts.swap || swapPending; // opts.swap: 같은 경로라도 페이지를 통째로 다시 그린다 (편집기 재로드)
    if (crossPath && !opts.replace && !opts.force) {
        const ev = new CustomEvent('calendar:before-navigate', { cancelable: true, detail: { href: u.href, path: u.pathname } });
        if (!document.dispatchEvent(ev)) return Promise.resolve(false); // dirty 편집기 등이 막았다 (확인 뒤 navigate(href, {force:true}) 로 다시 부른다)
    }
    const seq = ++navSeq;
    if (crossPath) swapInFlight = seq;
    const root = document.documentElement;
    root.classList.add('spa-loading');
    const hit = cache.get(u.href);
    const page = hit && Date.now() - hit.at < PREFETCH_TTL ? hit.promise : null;
    cache.delete(u.href);
    if (opts.replace) window.history.replaceState({ spa: true }, '', u.href);
    else { markCurrentEntry(); window.history.pushState({ spa: true }, '', u.href); }
    currentPath = u.pathname;
    shownHref = u.href;
    return (page || (crossPath ? fetchPage(u.href) : Promise.resolve(null)))
        .then((p) => {
            // 더 새로운 이동이 이미 화면을 바꿨다면 이 응답은 버린다. 아래의
            // afterSwap·calendar:navigated·catch·finally 는 모두 이 가드를
            // 이미 갖고 있었지만 **DOM 을 실제로 바꾸는 이 단계만** 빠져 있어,
            // 뒤늦게 도착한 옛 이동이 새 페이지를 제 HTML 로 덮어썼다 (주소는
            // 새 경로, 화면은 옛 문서). refreshGrid 의 자체 순번은 호출 시점에
            // 매겨지므로 이것을 잡지 못한다 — 늦게 부른 쪽이 더 큰 번호를 받는다.
            if (seq !== navSeq) return;
            if (p && p.url) {
                // 서버 리다이렉트(예: /crm/deals/:id → /crm/pipeline?deal=)를 따라간 경우 주소를 맞춘다
                let final = null;
                try { final = new URL(p.url); } catch (_) { final = null; }
                // fetch 의 응답 URL 에는 조각(#…)이 없다 — 브라우저의 리다이렉트처럼 요청의 조각을
                // 잇는다. 빼면 같은 주소를 다시 그릴 때마다 #c-<항목> 같은 조각이 떨어졌다 (BG6).
                if (final && !final.hash && u.hash) final.hash = u.hash;
                if (final && final.origin === window.location.origin && final.href !== u.href) { window.history.replaceState({ spa: true }, '', final.href); currentPath = final.pathname; shownHref = final.href; }
            }
            // alive: 페이지 교체는 새 화면의 <head> 스타일을 불러올 때까지(최대 1.5초) 기다린다 — 그 사이
            // 더 새 이동(뒤로 가기·다른 링크)이 시작됐으면 refreshGrid 가 교체를 버리도록 이 이동의
            // 생사를 넘긴다. 버려진 교체에는 아래의 afterSwap·calendar:navigated 도 없다 (navSeq 가드, R30).
            const alive = () => seq === navSeq;
            return refreshGrid(p ? { html: p.html, crossPath, keepMainScroll, alive } : { crossPath, keepMainScroll, alive });
        })
        .then(() => {
            // 이 이동의 화면이 들어왔다 (버려진 교체라면 seq 가 이미 낡았다).
            if (seq === navSeq && swapInFlight === seq) swapInFlight = 0;
        })
        .then(() => (afterSwap && seq === navSeq ? afterSwap() : undefined))
        .then(() => {
            if (seq !== navSeq) return;
            // 같은 경로를 다시 그렸다면 페이지 모듈이 붙은 **뒤에** 스크롤을 한 번 더 맞춘다 — 교체
            // 순간에는 모듈이 아직 칸을 고르지 않아(업무 평가 작성 화면은 js-eval 이 고르지 않은
            // 칸을 숨긴다) 본문이 짧고 scrollTop 이 0 으로 잘린다 (BG6). 맞출 자리는 교체 **직전**의
            // 자리다 — 받는 동안 사용자가 옮긴 스크롤이 이긴다 (BH5).
            const keptTop = keepMainScroll ? takeSwapScroll() : null;
            if (keptTop != null) {
                const main = document.querySelector('main.admin-main');
                if (main && main.scrollTop !== keptTop) main.scrollTop = keptTop;
            }
            // 앵커로 스크롤하는 것은 그 앵커에 **도착할** 때뿐이다 — 같은 주소를 제자리에서 다시
            // 그리는 것(swap: 저장 뒤 새로고침)은 보던 자리를 지킨다 (BG6).
            const target = u.hash && !opts.swap ? document.getElementById(decodeURIComponent(u.hash.slice(1))) : null;
            if (target) target.scrollIntoView({ block: 'center' }); // 레코드 페이지 안의 앵커 (#contact-…)
            else if (!opts.keepScroll) window.scrollTo({ top: 0 });
            document.dispatchEvent(new CustomEvent('calendar:navigated', { detail: { path: window.location.pathname, crossPath } }));
            // 교체를 기다리는 동안 떨군 배경 갱신(R31)을 새 화면이 모두 붙은 뒤 한 번 받는다: 루트가 있으면
            // 그 루트만(떨군 갱신과 같은 길), 없으면 라우터로 같은 주소를 다시 그려 afterSwap·calendar:navigated
            // 가 다시 돌게 한다 — 날 refreshGrid 의 페이지 교체는 calendar:navigated 로 붙는 모듈(조직도·인력정보 …)을
            // 떼어 둔 채 남긴다. 막힌 뒤로·앞으로 가기를 되감는 중이면(BH7) 그 되감기가 끝난 뒤에 받는다 —
            // 되감는 동안 주소는 떠나려던 칸이라 루트 갱신이 그 화면을 받아 오고, 다시 그릴 주소도 주소창이
            // 아니라 그려 둔 화면(shownHref)이다.
            if (takeDeferredRefresh()) {
                setTimeout(function owed() {
                    if (revertWait) { revertWait.promise.then(owed); return; }
                    if (hasRefreshRoot()) refreshOrReload();
                    else navigate(shownHref, { swap: true, replace: true, keepScroll: true });
                }, 0);
            }
        })
        // 실패하면 진짜 내비게이션으로 떨어진다 — 단, 더 새로운 이동이 이미 시작됐다면
        // 조용히 접는다 (뒤늦게 실패한 옛 이동이 사용자를 옛 주소로 끌고 가지 않게).
        .catch(() => { if (seq === navSeq) window.location.href = u.href; })
        .finally(() => {
            if (seq !== navSeq) return;
            root.classList.remove('spa-loading');
            const bar = document.getElementById('nav-progress'); // index.html 의 전체 로드용 막대: 링크 클릭에 켜졌다면 끈다
            if (bar) bar.classList.remove('active');
        });
}

// 관리자 화면: 페이지마다 표식을 넣는 대신 경로로 판정한다 (/admin/* 은 모두
// main.admin-main + #adminSidebar 껍데기를 쓴다). 제외 경로는 예전처럼 통째 이동.
export function adminSpaPage(pathname) {
    return /^\/admin(\/|$)/.test(pathname || '') && !excludedPath(pathname || '');
}

export function setupCalendarSpa() {
    if (!['calGrid', 'calTimeGrid', 'calList', 'projHub', 'crmBoard', 'recList', 'crmAccount', 'crmReports', 'crmContact', 'crmActivities', 'crmSettings', 'erpDoc', 'erpApprovals', 'erpAccounts', 'hrBoard', 'hrTeamCal', 'hrOrg', 'hrRecList', 'workHub', 'workList'].some((id) => document.getElementById(id))
        && !document.querySelector('.crm-page, .erp-page, .hr-page, .eval-main')
        && !(adminSpaPage(window.location.pathname) && document.querySelector('main.admin-main'))) return;
    currentPath = window.location.pathname;
    noteShown();
    trackHistory();
    // 순번 모드(Navigation API 없음)에서는 첫 칸에도 순번을 준다(BJ3) — 없으면 첫 이동 전에 생긴 조각 칸과
    // 첫 칸의 순서를 몰라, 뒤로 가기로 첫 칸에 돌아왔을 때 조각 분기가 붙인 번호가 다른 칸과 겹쳐 되감기가
    // 엉뚱한 칸에 내려앉았다. 이 replaceState 는 같은 문서 내비게이션을 하나 만들지만(markCurrentEntry 주석)
    // Navigation API 가 있는 브라우저(테스트의 Chromium 포함)에서는 하지 않는다.
    if (!window.navigation && stateIndex(window.history.state) == null) {
        rawReplace.call(window.history, withIndex(window.history.state, 0), '', window.location.href);
        noteShown();
    }
    // "이 화면의 클릭 가로채기가 걸렸다" 는 표식. 예전에는 초기화 때 걸던
    // history.replaceState({spa:true}) 가 그 역할을 겸했지만, 그것은 페이지 로드마다
    // same-document 내비게이션을 하나 만들어 "방금 클릭이 진짜 이동이었나" 를 보는
    // 코드·테스트를 속인다 (markCurrentEntry 주석 참조). 표식은 상태로만 남긴다.
    document.documentElement.dataset.spaReady = '1';

    document.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target.closest('a[href]');
        if (!a || a.target || a.hasAttribute('download') || a.dataset.noSpa != null) return;
        if (a.closest('#calendarModal, #calProjModal, #crmPanel, #hrPanel')) return; // 패널 안의 링크는 그대로
        if (!spaTarget(a.href)) return;
        e.preventDefault();
        navigate(a.href);
    });
    // GET 폼(필터 체크박스·검색)도 부분 교체로
    document.addEventListener('submit', (e) => {
        const f = e.target;
        // 이미 페이지가 처리한 제출(모듈이 preventDefault 한 인라인 추가 폼 등)은 건드리지 않는다 —
        // 가로채면 그 화면을 통째로 다시 그려 방금 만든 편집 상태가 날아간다.
        if (e.defaultPrevented) return;
        if (!(f instanceof HTMLFormElement) || (f.method || 'get').toLowerCase() !== 'get') return;
        if (f.dataset.noSpa != null) return;
        if (f.closest('#calendarModal, #calProjModal, #crmPanel')) return;
        const action = new URL(f.getAttribute('action') || window.location.pathname, window.location.href);
        if (!samePage(action.href)) return;
        e.preventDefault();
        const qs = new URLSearchParams(new FormData(f)).toString();
        navigate(action.pathname + (qs ? '?' + qs : ''), { keepScroll: true });
    });
    window.addEventListener('popstate', (e) => {
        // 막힌 뒤로·앞으로 가기를 한 칸 되감은 것 — 화면은 처음부터 그대로다 (BH7).
        if (revertTo != null) {
            const back = entryId() === revertTo;
            revertTo = null;
            endRevert();
            if (back) return;
        }
        // 같은 화면 안의 조각 이동(#앵커 링크, 주소창의 #… — Chromium 은 여기서도 popstate 를
        // 쏜다)은 화면을 다시 받지 않는다. 예전에는 같은 경로의 popstate 로 보고 통째로 다시
        // 받아, 그 사이 채운 입력 칸을 서버의 빈 칸으로 갈아끼웠다(업무 평가 e2e 의 준비
        // 단계가 가끔 빈 연결 설정을 저장한 원인, 백로그 BG1). 브라우저가 스크롤을 맡는다.
        {
            const now = new URL(window.location.href);
            const shown = new URL(shownHref, window.location.href);
            // 조각이 **달라야** 조각 이동이다 — 주소가 통째로 같은 popstate(패널이 ?deal= 을 밀었다가
            // 뒤로 닫기, 모듈이 쿼리를 제자리에서 고친 뒤 같은 주소의 앞 칸으로 가기)는 예전처럼
            // 아래에서 다시 받는다.
            if (now.pathname === shown.pathname && now.search === shown.search && now.hash !== shown.hash) {
                // 순번 모드에서 새 조각 칸은 순번이 없다 — 보던 칸 다음 번호를 이어 준다(이 칸을 지나는
                // 되감기의 걸음 수가 맞도록). 관찰한 조각 칸은 늘 새 칸이다.
                if (!window.navigation && rawReplace && stateIndex(window.history.state) == null && typeof shownId === 'number') {
                    rawReplace.call(window.history, withIndex(window.history.state, shownId + 1), '', window.location.href);
                }
                noteShown();
                return;
            }
        }
        // 제자리에서 처리할 대상: (1) SPA 가 만든 항목, (2) 같은 경로 안의 항목 —
        // 패널들이 라우터를 거치지 않고 스스로 밀어 넣는 ?deal=/?event=/?candidate= 가
        // 여기 해당한다. 그 항목들은 우리가 만든 것이 아니어서 state 가 없을 수 있고
        // (특히 페이지에 처음 도착한 항목), 그때 통째로 다시 읽으면 뒤로가기가 전체
        // 새로고침이 된다 (crm.mjs ux-2). 낯선 경로의 항목만 새로고침으로 넘긴다.
        if ((e.state && e.state.spa) || window.location.pathname === currentPath) {
            // 다른 경로로의 뒤로·앞으로도 링크와 같은 떠나기 확인을 거친다 (백로그 BG8). 같은
            // 경로 안(패널의 ?deal=·?candidate= 닫기)은 예전 그대로다. 막혔다면 화면은 그대로이니
            // 기록도 보고 있는 화면의 칸으로 되감는다 — 그 칸의 자리를 알면(Navigation API) 간 만큼
            // 거꾸로 가므로 앞 칸이 남고(BH7), 모르면 지금 주소를 새 칸으로 밀어 넣는다(앞 칸이
            // 잘린다). 확인한 쪽은 여느 때처럼 navigate(href, {force:true}) 로 다시 부른다.
            if (window.location.pathname !== currentPath) {
                const ev = new CustomEvent('calendar:before-navigate', { cancelable: true, detail: { href: window.location.href, path: window.location.pathname, popstate: true } });
                if (!document.dispatchEvent(ev)) {
                    revertToShown();
                    return;
                }
            } else {
                // 같은 경로의 뒤로·앞으로(편집기의 다른 ?dept= 칸)도 막을 수 있게 먼저 묻는다. 모듈이 창에
                // 단 popstate 리스너로는 늦다 — 창의 popstate 는 capture 여부와 무관하게 등록 순서로 불리고,
                // 여기서 모아 둔 페이지를 곧바로 되살리면 모듈의 signal 이 끊겨 그 리스너가 제 차례 전에 떨어진다.
                const ev = new CustomEvent('calendar:before-popstate', { cancelable: true, detail: { href: window.location.href, path: window.location.pathname, popstate: true } });
                if (!document.dispatchEvent(ev)) {
                    revertToShown();
                    return;
                }
            }
            navigate(window.location.href, { replace: true, keepScroll: true });
            return;
        }
        window.location.reload();
    });
    // hover 프리페치: 클릭 전에 받아 두면 전환이 즉시 일어난다 (같은 경로의 링크만 — 레코드 페이지는 무겁다)
    // [data-no-prefetch] 안의 링크는 받아 두지 않는다: 자동저장 편집기(인력정보)는 저장 전에 받아 둔
    // 화면이 20초 안에 다시 쓰이면 옛 if_version 을 들고 와 다음 저장이 헛되이 409 가 된다.
    document.addEventListener('mouseover', (e) => {
        const a = e.target.closest('a[href]');
        if (!a || a.closest('#calendarModal, [data-no-prefetch]') || !samePage(a.href) || a.href === window.location.href) return;
        prefetch(a.href);
    });
}
